import React, { useEffect, useState } from 'react';
import axios from '../../services/axios';
import toast from 'react-hot-toast';
import {
    PlusIcon,
    TrashIcon,
    VideoCameraIcon,
    AcademicCapIcon,
    UserGroupIcon,
    CheckCircleIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
    SparklesIcon,
    EyeIcon,
    QuestionMarkCircleIcon,
    PlayIcon,
    PencilIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';

function parseYouTubeId(url) {
    if (!url || typeof url !== 'string') return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
}

// How many choices an MCQ may offer. Mentors pick within this range per question.
const MIN_MCQ_OPTIONS = 2;
const MAX_MCQ_OPTIONS = 6;
const DEFAULT_MCQ_OPTIONS = 4;

function parseArrayOptions(val) {
    if (!val) return ['Option 1', 'Option 2', 'Option 3', 'Option 4'];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
        try {
            let parsed = JSON.parse(val);
            if (typeof parsed === 'string') {
                try { parsed = JSON.parse(parsed); } catch {}
            }
            if (Array.isArray(parsed)) return parsed;
        } catch {}
    }
    return ['Option 1', 'Option 2', 'Option 3', 'Option 4'];
}

export default function MentorLearningModules() {
    const [modules, setModules] = useState([]);
    const [interns, setInterns] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal state for module creation & editing
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [createLoading, setCreateLoading] = useState(false);
    const [pathItems, setPathItems] = useState([]);
    const [activeSectionTab, setActiveSectionTab] = useState(0);

    const REDO_SECTIONS = [
        { id: 0, title: '📌 Main Attempt (Initial Quiz)', shortTitle: 'Main Quiz', label: 'Attempt 1', description: 'Pool of questions given to interns on their initial attempt.' },
        { id: 1, title: '🔄 Redo Section 1', shortTitle: 'Redo 1', label: 'Retry 1 / Attempt 2', description: 'Questions given on 1st retake if intern fails the initial attempt.' },
        { id: 2, title: '🔄 Redo Section 2', shortTitle: 'Redo 2', label: 'Retry 2 / Attempt 3', description: 'Questions given on 2nd retake if intern fails the second attempt.' },
        { id: 3, title: '🔄 Redo Section 3', shortTitle: 'Redo 3', label: 'Retry 3 / Attempt 4', description: 'Questions given on 3rd retake if intern fails the third attempt.' }
    ];

    const [form, setForm] = useState({
        title: '',
        description: '',
        youtube_url: '',
        module_type: 'learning',
        duration_minutes: 30,
        passing_percentage: 90,
        learning_path_item_id: '',
        assigned_intern_ids: [],
        questions: []
    });

    // The choice count a mentor last picked. New MCQs adopt it, so setting the
    // first question to 3 choices means every question after it starts at 3.
    const [mcqOptionCount, setMcqOptionCount] = useState(DEFAULT_MCQ_OPTIONS);

    // Free-text filter over the intern picker. Only narrows what is shown --
    // interns already ticked stay selected while the search term changes.
    const [internSearch, setInternSearch] = useState('');

    // Submissions inspector modal
    const [selectedModuleSubmissions, setSelectedModuleSubmissions] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [modRes, intRes, pathRes] = await Promise.all([
                axios.get('/learning-modules/mentor/modules'),
                axios.get('/learning-modules/mentor/interns'),
                axios.get('/learning-modules/mentor/learning-path-items')
            ]);
            setModules(modRes.data.data || []);
            setInterns(intRes.data.data || []);
            setPathItems(pathRes.data.data || []);
        } catch (err) {
            toast.error('Failed to load learning modules');
        } finally {
            setLoading(false);
        }
    };

    // Auto-fill from selected path item
    const handleSelectPathItem = (itemId) => {
        if (!itemId) {
            setForm((prev) => ({ ...prev, learning_path_item_id: '' }));
            return;
        }
        const selected = pathItems.find((p) => Number(p.id) === Number(itemId));
        if (selected) {
            setForm((prev) => ({
                ...prev,
                learning_path_item_id: selected.id,
                title: prev.title ? prev.title : selected.title,
                description: prev.description ? prev.description : (selected.description || ''),
                duration_minutes: selected.required_hours ? Math.round(Number(selected.required_hours) * 60) : prev.duration_minutes
            }));
        }
    };

    // Toggle intern selection for assignment
    const handleToggleIntern = (internId) => {
        setForm((prev) => {
            const current = prev.assigned_intern_ids;
            if (current.includes(internId)) {
                return { ...prev, assigned_intern_ids: current.filter((id) => id !== internId) };
            } else {
                return { ...prev, assigned_intern_ids: [...current, internId] };
            }
        });
    };

    // Select/deselect acts on what the mentor can currently see, so searching
    // for a cohort and hitting "select all" does the obvious thing.
    const handleSelectAllInterns = (visibleInterns) => {
        const visibleIds = (visibleInterns || interns).map((i) => i.id);
        setForm((prev) => {
            const allVisibleSelected = visibleIds.length > 0
                && visibleIds.every((id) => prev.assigned_intern_ids.includes(id));
            if (allVisibleSelected) {
                return {
                    ...prev,
                    assigned_intern_ids: prev.assigned_intern_ids.filter((id) => !visibleIds.includes(id))
                };
            }
            return {
                ...prev,
                assigned_intern_ids: Array.from(new Set([...prev.assigned_intern_ids, ...visibleIds]))
            };
        });
    };

    // Add new question for active redo section
    const handleAddQuestion = (type = 'mcq') => {
        setForm((prev) => ({
            ...prev,
            questions: [
                ...prev.questions,
                {
                    question_type: type,
                    question_text: '',
                    options: type === 'mcq'
                        ? Array.from({ length: mcqOptionCount }, (_, n) => `Option ${n + 1}`)
                        : [],
                    correct_option_index: 0,
                    explanation: '',
                    redo_section: activeSectionTab
                }
            ]
        }));
    };

    // Copy Main Quiz questions to a Redo section
    const handleCopyMainQuestionsToSection = (targetSecId) => {
        const mainQuestions = form.questions.filter((q) => Number(q.redo_section || 0) === 0);
        if (mainQuestions.length === 0) {
            return toast.error('No main quiz questions available to copy');
        }
        const copied = mainQuestions.map((q) => ({
            ...q,
            id: undefined,
            redo_section: targetSecId
        }));
        setForm((prev) => ({
            ...prev,
            questions: [...prev.questions, ...copied]
        }));
        toast.success(`Copied ${mainQuestions.length} questions to ${REDO_SECTIONS[targetSecId].shortTitle}`);
    };

    // Remove question
    const handleRemoveQuestion = (originalIdx) => {
        setForm((prev) => ({
            ...prev,
            questions: prev.questions.filter((_, i) => i !== originalIdx)
        }));
    };

    // Update question field
    const handleUpdateQuestion = (originalIdx, field, value) => {
        setForm((prev) => {
            const updated = [...prev.questions];
            updated[originalIdx] = { ...updated[originalIdx], [field]: value };
            return { ...prev, questions: updated };
        });
    };

    // Update MCQ Option text
    const handleUpdateOption = (qIdx, optIdx, val) => {
        setForm((prev) => {
            const updated = [...prev.questions];
            const opts = [...parseArrayOptions(updated[qIdx].options)];
            opts[optIdx] = val;
            updated[qIdx] = { ...updated[qIdx], options: opts };
            return { ...prev, questions: updated };
        });
    };

    // Let the mentor choose how many choices an MCQ offers. Shrinking the list
    // can orphan the correct-answer pointer, so it is clamped back into range --
    // otherwise the question becomes impossible to answer correctly.
    const handleSetOptionCount = (qIdx, count) => {
        const next = Math.max(MIN_MCQ_OPTIONS, Math.min(MAX_MCQ_OPTIONS, Number(count) || MIN_MCQ_OPTIONS));
        setForm((prev) => {
            const updated = [...prev.questions];
            const current = parseArrayOptions(updated[qIdx].options);
            const opts = current.slice(0, next);
            while (opts.length < next) {
                opts.push(`Option ${opts.length + 1}`);
            }
            const correct = Math.min(Number(updated[qIdx].correct_option_index) || 0, next - 1);
            updated[qIdx] = { ...updated[qIdx], options: opts, correct_option_index: correct };
            return { ...prev, questions: updated };
        });
        // Remember it, so the next question the mentor adds matches this one.
        setMcqOptionCount(next);
    };

    // Always clear edit state when the form closes: a stale `editingModule`
    // turns the next "create" into an update of the module last opened.
    const handleCloseForm = () => {
        setIsCreateOpen(false);
        setEditingModule(null);
    };

    const handleOpenCreate = () => {
        setEditingModule(null);
        setActiveSectionTab(0);
        setInternSearch('');
        setMcqOptionCount(DEFAULT_MCQ_OPTIONS);
        setForm({
            title: '',
            description: '',
            youtube_url: '',
            module_type: 'learning',
            duration_minutes: 30,
            passing_percentage: 90,
            learning_path_item_id: '',
            assigned_intern_ids: [],
            questions: []
        });

        setIsCreateOpen(true);
    };

    const handleOpenEdit = (mod) => {
        setEditingModule(mod);
        setActiveSectionTab(0);
        setInternSearch('');
        let assignedIds = mod.assigned_intern_ids || [];
        if (typeof assignedIds === 'string') {
            try { assignedIds = JSON.parse(assignedIds); } catch { assignedIds = []; }
        }
        if (!Array.isArray(assignedIds)) assignedIds = [];

        let qList = mod.all_questions || mod.questions || [];
        if (typeof qList === 'string') {
            try { qList = JSON.parse(qList); } catch { qList = []; }
        }
        if (!Array.isArray(qList)) qList = [];

        setForm({
            title: mod.title || '',
            description: mod.description || '',
            youtube_url: mod.youtube_url || '',
            module_type: mod.module_type || 'learning',
            duration_minutes: mod.duration_minutes || 30,
            passing_percentage: mod.passing_percentage !== undefined ? Number(mod.passing_percentage) : 90,
            learning_path_item_id: mod.learning_path_item_id || '',
            assigned_intern_ids: assignedIds,
            questions: qList.map((q) => ({
                id: q.id,
                question_type: q.question_type || 'mcq',
                question_text: q.question_text || '',
                options: parseArrayOptions(q.options),
                correct_option_index: q.correct_option_index !== undefined ? Number(q.correct_option_index) : 0,
                explanation: q.explanation || '',
                redo_section: q.redo_section !== undefined ? Number(q.redo_section) : 0
            }))
        });

        // Match what this module already uses, so a question added during an
        // edit does not silently differ from the ones around it.
        const lastMcq = [...qList].reverse().find((q) => (q.question_type || 'mcq') === 'mcq');
        const existingCount = lastMcq ? parseArrayOptions(lastMcq.options).length : 0;
        setMcqOptionCount(
            existingCount >= MIN_MCQ_OPTIONS && existingCount <= MAX_MCQ_OPTIONS
                ? existingCount
                : DEFAULT_MCQ_OPTIONS
        );

        setIsCreateOpen(true);
    };

    // Create / Edit Module Submit
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return toast.error('Please enter module title');
        if (!form.youtube_url.trim()) return toast.error('Please enter YouTube video URL');
        
        const videoId = parseYouTubeId(form.youtube_url);
        if (!videoId) return toast.error('Invalid YouTube video URL format');

        try {
            setCreateLoading(true);
            let res;
            if (editingModule) {
                res = await axios.put(`/learning-modules/${editingModule.id}`, form);
                if (res.data.success) {
                    toast.success('Learning module updated successfully!');
                }
            } else {
                res = await axios.post('/learning-modules', form);
                if (res.data.success) {
                    toast.success('Learning module created successfully!');
                }
            }
            if (res.data.success) {
                setIsCreateOpen(false);
                setEditingModule(null);
                setForm({
                    title: '',
                    description: '',
                    youtube_url: '',
                    module_type: 'learning',
                    duration_minutes: 30,
                    passing_percentage: 90,
                    learning_path_item_id: '',
                    assigned_intern_ids: [],
                    questions: []
                });
                fetchData();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save module');
        } finally {
            setCreateLoading(false);
        }
    };

    // Delete Module
    const handleDeleteModule = async (moduleId) => {
        if (!window.confirm('Are you sure you want to delete this learning module?')) return;
        try {
            await axios.delete(`/learning-modules/${moduleId}`);
            toast.success('Module deleted');
            setModules((prev) => prev.filter((m) => m.id !== moduleId));
        } catch (err) {
            toast.error('Failed to delete module');
        }
    };

    const previewVideoId = parseYouTubeId(form.youtube_url);

    return (
        <div className="w-full space-y-6 pb-12">
            <PageHeader
                title="Intern Video Learning Modules"
                description="Create customized video learning modules for your assigned interns with anti-forwarding YouTube playback and auto-graded MCQ quizzes."
                actions={
                    <button
                        onClick={handleOpenCreate}
                        className="btn-primary inline-flex items-center gap-2"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Create Learning Module
                    </button>
                }
            />

            {/* Modules List */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[1, 2].map((i) => (
                        <div key={i} className="card p-6 space-y-4 animate-pulse">
                            <div className="h-6 bg-ink-200 rounded w-1/2" />
                            <div className="h-36 bg-ink-100 rounded-xl" />
                        </div>
                    ))}
                </div>
            ) : modules.length === 0 ? (
                <EmptyState
                    icon={VideoCameraIcon}
                    title="No Learning Modules Created Yet"
                    description="Create your first video module with embedded YouTube video and questions for your assigned interns."
                    action={
                        <button onClick={handleOpenCreate} className="btn-primary">
                            Create First Module
                        </button>
                    }
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {modules.map((m) => {
                        const questionsCount = m.questions ? m.questions.length : 0;
                        const submissionsCount = m.submissions ? m.submissions.length : 0;
                        const assignedCount = m.assigned_intern_ids && m.assigned_intern_ids.length > 0 ? m.assigned_intern_ids.length : 'All Interns';

                        return (
                            <div key={m.id} className="card p-5 border border-border flex flex-col justify-between space-y-4 shadow-sm hover:shadow-card-hover transition-all">
                                <div className="space-y-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="font-bold text-lg text-ink-950">{m.title}</h3>
                                            <p className="text-xs text-ink-500 mt-1 line-clamp-2">{m.description || 'No description provided.'}</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => handleOpenEdit(m)}
                                                className="p-1.5 text-ink-500 hover:text-brand-700 rounded-lg hover:bg-brand-50 transition-colors"
                                                title="Edit Module"
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteModule(m.id)}
                                                className="p-1.5 text-ink-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
                                                title="Delete Module"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Video Preview Frame */}
                                    <div className="relative aspect-video rounded-xl overflow-hidden bg-ink-900 border border-border group">
                                        <img
                                            src={`https://img.youtube.com/vi/${m.youtube_video_id}/hqdefault.jpg`}
                                            alt={m.title}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-ink-950/40 flex items-center justify-center group-hover:bg-ink-950/60 transition-colors">
                                            <div className="w-12 h-12 rounded-full bg-brand-700 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                                <PlayIcon className="w-6 h-6 translate-x-0.5" />
                                            </div>
                                        </div>
                                        <span className="absolute bottom-2 right-2 px-2 py-1 bg-black/75 text-white text-[10px] font-mono rounded">
                                            ID: {m.youtube_video_id}
                                        </span>
                                    </div>

                                    {/* Stats Chips */}
                                    <div className="flex flex-wrap items-center gap-2 text-xs pt-1">
                                        <span className={`px-2.5 py-1 rounded-lg font-semibold flex items-center gap-1 ${m.module_type === 'training' ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'bg-sky-50 text-sky-900 border border-sky-200'}`}>
                                            {m.module_type === 'training' ? '🛠️ Training Module' : '📘 Learning Module'}
                                        </span>

                                        {m.learningPathItem && (
                                            <span className="px-2.5 py-1 rounded-lg font-semibold bg-purple-50 text-purple-900 border border-purple-200 flex items-center gap-1">
                                                🎯 Linked: {m.learningPathItem.title}
                                            </span>
                                        )}
                                        {(() => {
                                            const qList = m.questions || [];
                                            const sec0Count = qList.filter(q => Number(q.redo_section || 0) === 0).length;
                                            const sec1Count = qList.filter(q => Number(q.redo_section || 0) === 1).length;
                                            const sec2Count = qList.filter(q => Number(q.redo_section || 0) === 2).length;
                                            const sec3Count = qList.filter(q => Number(q.redo_section || 0) === 3).length;

                                            return (
                                                <span className="px-2.5 py-1 bg-brand-50 text-brand-800 border border-brand-200 rounded-lg font-semibold flex items-center gap-1" title={`Main: ${sec0Count} | Redo1: ${sec1Count} | Redo2: ${sec2Count} | Redo3: ${sec3Count}`}>
                                                    <QuestionMarkCircleIcon className="w-4 h-4 text-brand-700" />
                                                    {questionsCount} Questions (Main: {sec0Count} • Redos: {sec1Count + sec2Count + sec3Count})
                                                </span>
                                            );
                                        })()}
                                        <span className="px-2.5 py-1 bg-surface text-ink-800 border border-border rounded-lg font-semibold flex items-center gap-1">
                                            ⏱️ {m.duration_minutes || 30} Mins Duration
                                        </span>
                                        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-900 border border-emerald-200 rounded-lg font-semibold flex items-center gap-1">
                                            🎯 Pass: {m.passing_percentage !== undefined ? m.passing_percentage : 90}%
                                        </span>
                                        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg font-semibold flex items-center gap-1">
                                            <CheckCircleIcon className="w-4 h-4 text-emerald-700" />
                                            {submissionsCount} Submissions
                                        </span>
                                        <span className="px-2.5 py-1 bg-surface text-ink-700 border border-border rounded-lg font-semibold flex items-center gap-1">
                                            <UserGroupIcon className="w-4 h-4 text-ink-500" />
                                            Assigned: {assignedCount}
                                        </span>
                                    </div>
                                </div>

                                <div className="pt-3 border-t border-border flex items-center justify-between">
                                    <span className="text-xs text-ink-400">Created: {new Date(m.created_at).toLocaleDateString()}</span>
                                    <button
                                        onClick={() => setSelectedModuleSubmissions(m)}
                                        className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                                    >
                                        <EyeIcon className="w-4 h-4" />
                                        View Submissions ({submissionsCount})
                                    </button>
                                </div>
                            </div>
                        );
                    })}

                    {/* Adding another module is the common next action, so put
                        it where the mentor is already looking rather than only
                        in the page header. */}
                    <button
                        type="button"
                        onClick={handleOpenCreate}
                        className="card p-5 border-2 border-dashed border-border text-ink-500 hover:border-brand-400 hover:text-brand-700 hover:bg-brand-50/30 transition-colors flex flex-col items-center justify-center gap-2 min-h-[200px]"
                    >
                        <PlusIcon className="w-8 h-8" />
                        <span className="font-bold text-sm">Add Another Module</span>
                        <span className="text-xs text-ink-400 text-center max-w-[15rem]">
                            You can add as many as you need, including several videos on the same learning step.
                        </span>
                    </button>
                </div>
            )}

            {/* CREATE MODULE MODAL */}
            {isCreateOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-ink-950/60 backdrop-blur-sm flex justify-center items-center p-4 sm:p-6 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl border border-border w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="p-6 bg-gradient-to-r from-ink-950 via-brand-950 to-brand-900 text-white flex justify-between items-center border-b border-brand-800">
                            <div>
                                <h3 className="text-xl font-bold flex items-center gap-2">
                                    <VideoCameraIcon className="w-6 h-6 text-brand-400" />
                                    {editingModule ? 'Edit Video Learning Module' : 'Create New Video Learning Module'}
                                </h3>
                                <p className="text-xs text-brand-200 mt-1">Set up YouTube video, target interns, and auto-graded quiz questions.</p>
                            </div>
                            <button
                                onClick={handleCloseForm}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Form Body */}
                        <form onSubmit={handleCreateSubmit} className="p-6 overflow-y-auto flex-1 space-y-6">
                            {/* Basic Details */}
                            <div className="space-y-4">
                                <h4 className="font-bold text-xs uppercase tracking-wider text-ink-950 border-b border-border pb-2 flex items-center gap-2">
                                    <SparklesIcon className="w-4 h-4 text-brand-700" />
                                    1. Module Metadata & YouTube Video
                                </h4>

                                <div className="p-3 bg-brand-50/60 rounded-xl border border-brand-200">
                                    <label className="input-label font-bold text-brand-900 flex items-center justify-between">
                                        <span>Target Learning & Training Page Module</span>
                                        <span className="text-[10px] text-brand-700 font-normal">Unlocks item on intern /learning path</span>
                                    </label>
                                    <select
                                        value={form.learning_path_item_id || ''}
                                        onChange={(e) => handleSelectPathItem(e.target.value)}
                                        className="input-field w-full text-xs font-semibold bg-white border-brand-300 focus:border-brand-600 mt-1"
                                    >
                                        <option value="">-- Select Learning & Training Path Item --</option>
                                        {pathItems.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                🎯 {p.title} ({p.type} • {p.required_hours} hrs)
                                                {p.module_count > 0
                                                    ? ` — ${p.module_count} video${p.module_count === 1 ? '' : 's'} already added`
                                                    : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-[11px] text-ink-500 mt-1">
                                        A step can hold more than one video. Pick the same step again to add another.
                                    </p>
                                </div>

                                <div>
                                    <label className="input-label">Module Title *</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Introduction to React State Management"
                                        value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                        className="input-field w-full"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="input-label">Description / Instructions <span className="text-ink-400 font-normal">(Optional)</span></label>
                                    <textarea
                                        rows={3}
                                        placeholder="Explain what the intern will learn from this video (optional)..."
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                        className="input-field w-full"
                                    />
                                </div>

                                <div>
                                    <label className="input-label">YouTube Video Link *</label>
                                    <input
                                        type="url"
                                        placeholder="e.g. https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                                        value={form.youtube_url}
                                        onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                                        className="input-field w-full font-mono text-xs"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                                    <div>
                                        <label className="input-label">Module Type *</label>
                                        <select
                                            value={form.module_type}
                                            onChange={(e) => setForm({ ...form, module_type: e.target.value })}
                                            className="input-field w-full text-xs font-semibold"
                                        >
                                            <option value="learning">📘 Learning Module</option>
                                            <option value="training">🛠️ Training Module</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="input-label">Module Duration (Mins) *</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="600"
                                            value={form.duration_minutes}
                                            onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
                                            className="input-field w-full text-xs"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="input-label">Passing Score (%) *</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={form.passing_percentage}
                                            onChange={(e) => setForm({ ...form, passing_percentage: Number(e.target.value) })}
                                            className="input-field w-full text-xs font-bold text-brand-800"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Live Video Preview */}
                                {previewVideoId ? (
                                    <div className="p-3 bg-brand-50/60 rounded-xl border border-brand-200 space-y-2">
                                        <div className="text-xs font-semibold text-brand-900 flex items-center gap-1.5">
                                            <CheckCircleIcon className="w-4 h-4 text-emerald-600" /> Valid YouTube ID detected: <code className="bg-white px-1.5 py-0.5 rounded border">{previewVideoId}</code>
                                        </div>
                                        <div className="aspect-video rounded-lg overflow-hidden bg-black max-w-md mx-auto">
                                            <iframe
                                                src={`https://www.youtube.com/embed/${previewVideoId}`}
                                                title="Preview"
                                                className="w-full h-full"
                                                allowFullScreen
                                            />
                                        </div>
                                    </div>
                                ) : form.youtube_url ? (
                                    <div className="text-xs text-rose-600 bg-rose-50 p-2.5 rounded-lg border border-rose-200">
                                        Could not parse YouTube Video ID from URL. Ensure format is https://www.youtube.com/watch?v=...
                                    </div>
                                ) : null}
                            </div>

                            {/* Intern Assignment */}
                            {(() => {
                                const term = internSearch.trim().toLowerCase();
                                const visibleInterns = term
                                    ? interns.filter((i) => {
                                        const haystack = [
                                            i.first_name, i.last_name,
                                            `${i.first_name || ''} ${i.last_name || ''}`,
                                            i.email,
                                            i.profile?.university, i.profile?.major, i.profile?.student_id
                                        ].filter(Boolean).join(' ').toLowerCase();
                                        return haystack.includes(term);
                                    })
                                    : interns;
                                const allVisibleSelected = visibleInterns.length > 0
                                    && visibleInterns.every((i) => form.assigned_intern_ids.includes(i.id));

                                return (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between border-b border-border pb-2">
                                    <h4 className="font-bold text-xs uppercase tracking-wider text-ink-950 flex items-center gap-2">
                                        <UserGroupIcon className="w-4 h-4 text-brand-700" />
                                        2. Assign to Interns ({form.assigned_intern_ids.length} Selected)
                                    </h4>
                                    <button
                                        type="button"
                                        onClick={() => handleSelectAllInterns(visibleInterns)}
                                        className="text-xs font-bold text-brand-700 hover:underline"
                                        disabled={visibleInterns.length === 0}
                                    >
                                        {allVisibleSelected
                                            ? (term ? 'Deselect These' : 'Deselect All')
                                            : (term ? `Select These (${visibleInterns.length})` : 'Select All Interns')}
                                    </button>
                                </div>

                                {interns.length === 0 ? (
                                    <p className="text-xs text-ink-400 italic">No assigned interns found. The module will be available to all interns.</p>
                                ) : (
                                    <>
                                    <div className="relative">
                                        <MagnifyingGlassIcon
                                            className="w-4 h-4 text-ink-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                                            aria-hidden="true"
                                        />
                                        <input
                                            type="text"
                                            value={internSearch}
                                            onChange={(e) => setInternSearch(e.target.value)}
                                            placeholder="Search interns by name, email or university..."
                                            aria-label="Search interns"
                                            className="input-field w-full text-xs pl-9 pr-9"
                                        />
                                        {internSearch && (
                                            <button
                                                type="button"
                                                onClick={() => setInternSearch('')}
                                                aria-label="Clear intern search"
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                                            >
                                                <XMarkIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    {term && (
                                        <p className="text-[11px] text-ink-500">
                                            {visibleInterns.length === 0
                                                ? 'No interns match that search.'
                                                : `Showing ${visibleInterns.length} of ${interns.length} interns. Selections outside this search are kept.`}
                                        </p>
                                    )}

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-40 overflow-y-auto p-1">
                                        {visibleInterns.map((i) => {
                                            const isSelected = form.assigned_intern_ids.includes(i.id);
                                            return (
                                                <div
                                                    key={i.id}
                                                    onClick={() => handleToggleIntern(i.id)}
                                                    className={`p-2.5 rounded-xl border cursor-pointer flex items-center justify-between text-xs transition-all ${isSelected ? 'bg-brand-50 border-brand-300 text-brand-950 font-bold' : 'bg-surface border-border text-ink-700 hover:bg-white'}`}
                                                >
                                                    <div>
                                                        <div>{i.first_name} {i.last_name}</div>
                                                        <div className="text-[11px] text-ink-400 font-normal">{i.email}</div>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {}}
                                                        className="rounded text-brand-700 focus:ring-brand-500"
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                    </>
                                )}
                            </div>
                                );
                            })()}

                            {/* Questions Builder */}
                            <div className="space-y-4">
                                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-2">
                                    <h4 className="font-bold text-xs uppercase tracking-wider text-ink-950 flex items-center gap-2">
                                        <AcademicCapIcon className="w-4 h-4 text-brand-700" />
                                        3. Module Questions & Redo Pools ({form.questions.length} Total)
                                    </h4>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleAddQuestion('mcq')}
                                            title={`New MCQs start with ${mcqOptionCount} answer choices. Change a question's "Choices" dropdown to set this.`}
                                            className="btn-secondary py-1 px-3 text-xs flex items-center gap-1"
                                        >
                                            <PlusIcon className="w-3.5 h-3.5" /> Add MCQ ({mcqOptionCount})
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleAddQuestion('short_answer')}
                                            className="btn-secondary py-1 px-3 text-xs flex items-center gap-1"
                                        >
                                            <PlusIcon className="w-3.5 h-3.5" /> Add Open Question
                                        </button>
                                    </div>
                                </div>

                                {/* Section Tabs */}
                                <div className="flex flex-wrap items-center gap-1.5 bg-surface p-1.5 rounded-xl border border-border">
                                    {REDO_SECTIONS.map((sec) => {
                                        const count = form.questions.filter((q) => Number(q.redo_section || 0) === sec.id).length;
                                        const isActive = activeSectionTab === sec.id;
                                        return (
                                            <button
                                                key={sec.id}
                                                type="button"
                                                onClick={() => setActiveSectionTab(sec.id)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                                                    isActive
                                                        ? 'bg-brand-600 text-white shadow-xs'
                                                        : 'hover:bg-white text-ink-700'
                                                }`}
                                            >
                                                <span>{sec.shortTitle}</span>
                                                <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${isActive ? 'bg-white/20 text-white font-bold' : 'bg-ink-200 text-ink-800'}`}>
                                                    {count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Section Info Banner */}
                                <div className="p-3 bg-brand-50/60 rounded-xl border border-brand-200 flex flex-wrap items-center justify-between gap-2 text-xs">
                                    <div>
                                        <span className="font-bold text-brand-900">{REDO_SECTIONS[activeSectionTab].title}</span>
                                        <p className="text-[11px] text-brand-700">{REDO_SECTIONS[activeSectionTab].description}</p>
                                    </div>
                                    {activeSectionTab > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => handleCopyMainQuestionsToSection(activeSectionTab)}
                                            className="px-2.5 py-1 text-[11px] font-bold bg-white text-brand-800 border border-brand-300 rounded-lg hover:bg-brand-100 transition-colors shadow-xs"
                                        >
                                            📋 Copy Main Quiz Questions to {REDO_SECTIONS[activeSectionTab].shortTitle}
                                        </button>
                                    )}
                                </div>

                                {(() => {
                                    const filteredList = form.questions
                                        .map((q, originalIdx) => ({ q, originalIdx }))
                                        .filter(({ q }) => Number(q.redo_section || 0) === activeSectionTab);

                                    if (filteredList.length === 0) {
                                        return (
                                            <div className="p-6 text-center text-xs text-ink-500 bg-surface rounded-xl border border-dashed border-border space-y-1">
                                                <p className="font-medium text-ink-700">No questions added in {REDO_SECTIONS[activeSectionTab].shortTitle} pool yet.</p>
                                                <p className="text-[11px] text-ink-400">
                                                    {activeSectionTab === 0
                                                        ? 'Click "Add MCQ" above to add main attempt questions.'
                                                        : `If left empty, intern retakes will automatically fallback to the Main Quiz pool. Or click "Add MCQ" or "Copy Main Quiz Questions" above to customize redo questions.`}
                                                </p>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="space-y-4">
                                            {filteredList.map(({ q, originalIdx }, sectionIdx) => (
                                                <div key={originalIdx} className="p-4 bg-white border border-border rounded-xl space-y-3 shadow-xs relative">
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-bold text-brand-800 bg-brand-50 px-2.5 py-0.5 rounded uppercase tracking-wider">
                                                                Q{sectionIdx + 1}: {q.question_type === 'mcq' ? 'Multiple Choice (MCQ)' : 'Open / Short Answer'}
                                                            </span>
                                                            <span className="text-[11px] font-semibold text-ink-500 bg-surface px-2 py-0.5 rounded border border-border">
                                                                {REDO_SECTIONS[Number(q.redo_section || 0)]?.shortTitle || 'Main Quiz'}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            <select
                                                                value={Number(q.redo_section || 0)}
                                                                onChange={(e) => handleUpdateQuestion(originalIdx, 'redo_section', Number(e.target.value))}
                                                                className="input-field text-[11px] font-semibold py-1 px-2 bg-surface border-border"
                                                                title="Move to another Redo section pool"
                                                            >
                                                                <option value={0}>📌 Move to Main Quiz</option>
                                                                <option value={1}>🔄 Move to Redo 1</option>
                                                                <option value={2}>🔄 Move to Redo 2</option>
                                                                <option value={3}>🔄 Move to Redo 3</option>
                                                            </select>

                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveQuestion(originalIdx)}
                                                                className="text-rose-600 hover:text-rose-800 text-xs font-semibold flex items-center gap-1 p-1 hover:bg-rose-50 rounded"
                                                            >
                                                                <TrashIcon className="w-3.5 h-3.5" /> Remove
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="text-[11px] font-semibold text-ink-600 block mb-1">Question Text</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Enter the question text..."
                                                            value={q.question_text}
                                                            onChange={(e) => handleUpdateQuestion(originalIdx, 'question_text', e.target.value)}
                                                            className="input-field w-full text-xs"
                                                            required
                                                        />
                                                    </div>

                                                    {/* MCQ Choices */}
                                                    {q.question_type === 'mcq' && (
                                                        <div className="space-y-2 pt-1">
                                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                                <label className="text-[11px] font-semibold text-ink-600">
                                                                    MCQ Options (Select radio button for the CORRECT option)
                                                                </label>
                                                                <label className="flex items-center gap-1.5 text-[11px] font-semibold text-ink-600">
                                                                    Choices:
                                                                    <select
                                                                        value={parseArrayOptions(q.options).length || DEFAULT_MCQ_OPTIONS}
                                                                        onChange={(e) => handleSetOptionCount(originalIdx, e.target.value)}
                                                                        aria-label="Number of answer choices"
                                                                        className="input-field text-xs py-1 pr-6"
                                                                    >
                                                                        {Array.from(
                                                                            { length: MAX_MCQ_OPTIONS - MIN_MCQ_OPTIONS + 1 },
                                                                            (_, n) => MIN_MCQ_OPTIONS + n
                                                                        ).map((n) => (
                                                                            <option key={n} value={n}>{n}</option>
                                                                        ))}
                                                                    </select>
                                                                </label>
                                                            </div>
                                                            <div className="space-y-2">
                                                                {parseArrayOptions(q.options).map((optText, optIdx) => (
                                                                    <div key={optIdx} className="flex items-center gap-2">
                                                                        <input
                                                                            type="radio"
                                                                            name={`correct_opt_${originalIdx}`}
                                                                            checked={Number(q.correct_option_index) === optIdx}
                                                                            onChange={() => handleUpdateQuestion(originalIdx, 'correct_option_index', optIdx)}
                                                                            className="text-emerald-600 focus:ring-emerald-500 w-4 h-4 cursor-pointer"
                                                                        />
                                                                        <input
                                                                            type="text"
                                                                            placeholder={`Option ${optIdx + 1}`}
                                                                            value={optText}
                                                                            onChange={(e) => handleUpdateOption(originalIdx, optIdx, e.target.value)}
                                                                            className={`input-field flex-1 text-xs ${Number(q.correct_option_index) === optIdx ? 'border-emerald-500 bg-emerald-50/40 font-semibold' : ''}`}
                                                                            required
                                                                        />
                                                                        {Number(q.correct_option_index) === optIdx && (
                                                                            <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
                                                                                ✓ Correct Answer
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            <div className="pt-2">
                                                                <label className="text-[11px] font-semibold text-ink-600 block mb-1">Answer Explanation (Optional)</label>
                                                                <input
                                                                    type="text"
                                                                    placeholder="Explanation shown to intern after submission..."
                                                                    value={q.explanation || ''}
                                                                    onChange={(e) => handleUpdateQuestion(originalIdx, 'explanation', e.target.value)}
                                                                    className="input-field w-full text-xs"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Modal Actions */}
                            <div className="pt-4 border-t border-border flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateOpen(false)}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createLoading}
                                    className="btn-primary"
                                >
                                    {createLoading ? 'Saving Module...' : 'Publish Learning Module'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* SUBMISSIONS INSPECTOR MODAL */}
            {selectedModuleSubmissions && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-ink-950/60 backdrop-blur-sm flex justify-center items-center p-4 sm:p-6 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl border border-border w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="p-5 bg-gradient-to-r from-ink-950 to-brand-900 text-white flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-lg">{selectedModuleSubmissions.title} - Intern Progress</h3>
                                <p className="text-xs text-brand-200">Track video watch status & quiz completion scores</p>
                            </div>
                            <button
                                onClick={() => setSelectedModuleSubmissions(null)}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-4">
                            {(!selectedModuleSubmissions.submissions || selectedModuleSubmissions.submissions.length === 0) ? (
                                <div className="p-8 text-center text-xs text-ink-500 bg-surface rounded-xl border border-dashed border-border">
                                    No intern submissions recorded for this module yet.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {selectedModuleSubmissions.submissions.map((sub) => (
                                        <div key={sub.id} className="p-4 bg-white border border-border rounded-xl flex items-center justify-between text-xs space-y-1">
                                            <div>
                                                <div className="font-bold text-ink-950 text-sm">
                                                    {sub.intern ? `${sub.intern.first_name} ${sub.intern.last_name}` : `Intern #${sub.intern_user_id}`}
                                                </div>
                                                <div className="text-ink-500">{sub.intern?.email}</div>
                                                <div className="flex items-center gap-2 mt-1.5">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${sub.video_watched ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                                                        {sub.video_watched ? '✓ Video Completed' : `Video Watched: ${Math.round(sub.max_time_watched || 0)}s`}
                                                    </span>
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${sub.is_passed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : (sub.is_submitted ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-surface text-ink-600 border border-border')}`}>
                                                        {sub.is_passed ? '✓ Passed' : (sub.is_submitted ? '✕ Failed (Retake Allowed)' : 'Quiz Not Submitted')}
                                                    </span>
                                                </div>
                                            </div>

                                            {sub.is_submitted && (
                                                <div className="text-right">
                                                    <div className="text-[10px] text-ink-500 uppercase font-bold">Quiz Score</div>
                                                    <div className="text-lg font-bold text-brand-700">
                                                        {sub.score} / {sub.total_questions} ({sub.percentage}%)
                                                    </div>
                                                    <div className="text-[11px] text-ink-400">{new Date(sub.submitted_at).toLocaleDateString()}</div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
